import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, RefreshCw, Layers } from "lucide-react";
import { useResponsiveTickInterval } from "@/lib/chart-utils";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useAdoptionPipeline } from "@/hooks/use-population-insights";
import { useMemo, useState } from "react";
import { useToggleableSeries, InteractiveLegend, type LegendSeries } from "./interactive-legend";
import { format, parseISO, addDays } from "date-fns";
import {
  type ComparisonMode,
  getComparisonDays,
  getComparisonPeriodLabel,
  LOCATION_SERIES_COLORS,
  formatTrendDate,
} from "@/lib/trend-comparison";

type SpeciesFilter = "all" | "DOG" | "CAT" | "OTHER";

const LEVEL_COLORS: Record<string, string> = {
  L1: "hsl(142 70% 45%)",
  L2: "hsl(210 70% 50%)",
  L3: "hsl(38 92% 50%)",
  L4: "hsl(0 72% 51%)",
  unknown: "hsl(var(--muted-foreground))",
};

const LEVEL_LABELS: Record<string, string> = {
  L1: "L1 Easy",
  L2: "L2 Moderate",
  L3: "L3 Challenging",
  L4: "L4 Expert",
  unknown: "Unassigned",
};

const LEVEL_KEYS = ["L1", "L2", "L3", "L4", "unknown"] as const;

const LEVEL_SERIES: LegendSeries[] = LEVEL_KEYS.map(key => ({
  key,
  label: LEVEL_LABELS[key],
  color: LEVEL_COLORS[key],
}));

interface AdoptionPipelineChartProps {
  days: number;
  showSystem: boolean;
  selectedLocations: string[];
  comparisonMode: ComparisonMode;
  locations: Array<{ code: string; label: string }>;
}

function StatCard({ label, value, color, testId }: { label: string; value: number; color?: string; testId: string }) {
  return (
    <div className="flex flex-col p-3 rounded-md border" data-testid={testId}>
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-lg font-semibold" style={color ? { color } : undefined}>{value}</span>
    </div>
  );
}

interface ChartDataPoint {
  date: string;
  formattedDate: string;
  L1: number;
  L2: number;
  L3: number;
  L4: number;
  unknown: number;
  total: number;
  _prevTotal?: number | null;
}

function extractSpeciesValues(
  p: any,
  species: SpeciesFilter,
): { L1: number; L2: number; L3: number; L4: number; unknown: number } {
  if (species === "all") {
    return { L1: p.L1, L2: p.L2, L3: p.L3, L4: p.L4, unknown: p.unknown };
  }
  const sp = p.bySpecies?.[species];
  if (!sp) return { L1: 0, L2: 0, L3: 0, L4: 0, unknown: 0 };
  return { L1: sp.L1, L2: sp.L2, L3: sp.L3, L4: sp.L4, unknown: sp.unknown };
}

function SingleLocationChart({
  days,
  location,
  comparisonMode,
  speciesFilter,
  label,
  color,
}: {
  days: number;
  location?: string;
  comparisonMode: ComparisonMode;
  speciesFilter: SpeciesFilter;
  label?: string;
  color?: string;
}) {
  const comparisonDays = getComparisonDays(comparisonMode);
  const fetchDays = comparisonMode !== "none" ? days + comparisonDays : days;
  const { data, isLoading, isError, refetch } = useAdoptionPipeline(fetchDays, location);

  const chartData = useMemo((): ChartDataPoint[] => {
    if (!data?.series) return [];
    const allPoints = data.series.map((p) => {
      const vals = extractSpeciesValues(p, speciesFilter);
      return {
        date: p.date,
        formattedDate: formatTrendDate(p.date),
        ...vals,
        total: vals.L1 + vals.L2 + vals.L3 + vals.L4 + vals.unknown,
      };
    });

    if (comparisonMode === "none" || comparisonDays === 0) {
      return allPoints.slice(-days);
    }

    const fullMap = new Map<string, (typeof allPoints)[0]>();
    for (const row of allPoints) fullMap.set(row.date, row);
    const currentSlice = allPoints.slice(-days);

    return currentSlice.map((row) => {
      const prevDate = format(addDays(parseISO(row.date), -comparisonDays), "yyyy-MM-dd");
      const prevRow = fullMap.get(prevDate);
      return { ...row, _prevTotal: prevRow?.total ?? null };
    });
  }, [data, days, comparisonMode, comparisonDays, speciesFilter]);

  const hasData = chartData.length > 0;
  const hasPrev = comparisonMode !== "none" && chartData.some((d) => d._prevTotal != null);
  const tickInterval = useResponsiveTickInterval(chartData.length);

  const allSeries = useMemo(() => {
    if (!hasPrev) return LEVEL_SERIES;
    return [...LEVEL_SERIES, { key: "_prevTotal", label: `Prev Total (${getComparisonPeriodLabel(comparisonMode)})`, color: "hsl(var(--foreground))" }];
  }, [hasPrev, comparisonMode]);
  const { visibleSeries, toggleSeries } = useToggleableSeries(allSeries);

  if (isLoading) return <Skeleton className="h-[280px] w-full" />;
  if (isError) {
    return (
      <Alert variant="destructive">
        <AlertTriangle className="h-4 w-4" />
        <AlertDescription className="flex items-center gap-2">
          Failed to load{label ? ` ${label}` : ""} data.
          <Button variant="outline" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
          </Button>
        </AlertDescription>
      </Alert>
    );
  }

  if (!hasData) {
    return (
      <div className="flex items-center justify-center h-[200px] text-muted-foreground">
        No data available for the selected period.
      </div>
    );
  }

  return (
    <div>
      {label && (
        <p className="text-sm font-medium mb-2" style={color ? { color } : undefined}>
          {label}
        </p>
      )}
      <InteractiveLegend
        series={allSeries}
        visibleSeries={visibleSeries}
        onToggle={toggleSeries}
        compact
        testIdPrefix="adoption-legend"
      />
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis dataKey="formattedDate" tick={{ fontSize: 12 }} className="text-muted-foreground" interval={tickInterval} />
          <YAxis tick={{ fontSize: 12 }} className="text-muted-foreground" />
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--card))",
              borderColor: "hsl(var(--border))",
              borderRadius: "6px",
              color: "hsl(var(--card-foreground))",
            }}
          />
          {LEVEL_KEYS.filter(k => visibleSeries.has(k)).map((key) => (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              name={LEVEL_LABELS[key]}
              stackId="1"
              stroke={LEVEL_COLORS[key]}
              fill={LEVEL_COLORS[key]}
              fillOpacity={key === "unknown" ? 0.3 : 0.6}
            />
          ))}
          {hasPrev && visibleSeries.has("_prevTotal") && (
            <Line
              type="monotone"
              dataKey="_prevTotal"
              name={`Previous Total (${getComparisonPeriodLabel(comparisonMode)})`}
              stroke="hsl(var(--foreground))"
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={false}
              connectNulls
              legendType="line"
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function AdoptionChartWithLegend({ chartData, hasPrev, comparisonMode }: { chartData: ChartDataPoint[]; hasPrev: boolean; comparisonMode: ComparisonMode }) {
  const tickInterval = useResponsiveTickInterval(chartData.length);
  const allSeries = useMemo(() => {
    if (!hasPrev) return LEVEL_SERIES;
    return [...LEVEL_SERIES, { key: "_prevTotal", label: `Prev Total (${getComparisonPeriodLabel(comparisonMode)})`, color: "hsl(var(--foreground))" }];
  }, [hasPrev, comparisonMode]);
  const { visibleSeries, toggleSeries } = useToggleableSeries(allSeries);
  return (
    <>
      <InteractiveLegend
        series={allSeries}
        visibleSeries={visibleSeries}
        onToggle={toggleSeries}
        testIdPrefix="adoption-legend"
      />
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis dataKey="formattedDate" tick={{ fontSize: 12 }} className="text-muted-foreground" interval={tickInterval} />
          <YAxis tick={{ fontSize: 12 }} className="text-muted-foreground" />
          <Tooltip
            contentStyle={{
              backgroundColor: "hsl(var(--card))",
              borderColor: "hsl(var(--border))",
              borderRadius: "6px",
              color: "hsl(var(--card-foreground))",
            }}
          />
          {LEVEL_KEYS.filter(k => visibleSeries.has(k)).map((key) => (
            <Area
              key={key}
              type="monotone"
              dataKey={key}
              name={LEVEL_LABELS[key]}
              stackId="1"
              stroke={LEVEL_COLORS[key]}
              fill={LEVEL_COLORS[key]}
              fillOpacity={key === "unknown" ? 0.3 : 0.6}
            />
          ))}
          {hasPrev && visibleSeries.has("_prevTotal") && (
            <Line
              type="monotone"
              dataKey="_prevTotal"
              name={`Previous Total (${getComparisonPeriodLabel(comparisonMode)})`}
              stroke="hsl(var(--foreground))"
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={false}
              connectNulls
              legendType="line"
            />
          )}
        </AreaChart>
      </ResponsiveContainer>
    </>
  );
}

export function AdoptionPipelineChart({ days, showSystem, selectedLocations, comparisonMode, locations }: AdoptionPipelineChartProps) {
  const showLocationPanels = selectedLocations.length > 0;
  const [speciesFilter, setSpeciesFilter] = useState<SpeciesFilter>("all");

  const location = showSystem ? undefined : (selectedLocations.length === 1 ? selectedLocations[0] : undefined);

  const comparisonDays = getComparisonDays(comparisonMode);
  const fetchDays = comparisonMode !== "none" ? days + comparisonDays : days;
  const { data, isLoading, isError, refetch } = useAdoptionPipeline(fetchDays, location);

  const chartData = useMemo((): ChartDataPoint[] => {
    if (!data?.series) return [];
    const allPoints = data.series.map((p) => {
      const vals = extractSpeciesValues(p, speciesFilter);
      return {
        date: p.date,
        formattedDate: formatTrendDate(p.date),
        ...vals,
        total: vals.L1 + vals.L2 + vals.L3 + vals.L4 + vals.unknown,
      };
    });

    if (comparisonMode === "none" || comparisonDays === 0) {
      return allPoints.slice(-days);
    }

    const fullMap = new Map<string, (typeof allPoints)[0]>();
    for (const row of allPoints) fullMap.set(row.date, row);
    const currentSlice = allPoints.slice(-days);

    return currentSlice.map((row) => {
      const prevDate = format(addDays(parseISO(row.date), -comparisonDays), "yyyy-MM-dd");
      const prevRow = fullMap.get(prevDate);
      return { ...row, _prevTotal: prevRow?.total ?? null };
    });
  }, [data, days, comparisonMode, comparisonDays, speciesFilter]);

  const latest = useMemo(() => {
    if (!data?.latest) return null;
    if (speciesFilter === "all") return data.latest;
    const sp = data.latest.bySpecies?.[speciesFilter];
    if (!sp) return null;
    return {
      L1: sp.L1,
      L2: sp.L2,
      L3: sp.L3,
      L4: sp.L4,
      unknown: sp.unknown,
      total: sp.L1 + sp.L2 + sp.L3 + sp.L4 + sp.unknown,
    };
  }, [data, speciesFilter]);

  const hasData = chartData.length > 0;
  const hasPrev = comparisonMode !== "none" && chartData.some((d) => d._prevTotal != null);

  const getLocationLabel = (code: string) => locations.find((l) => l.code === code)?.label || code;

  if (isLoading && !showLocationPanels) {
    return (
      <Card data-testid="card-adoption-pipeline">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Adoption Levels
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  if (isError && !showLocationPanels) {
    return (
      <Card data-testid="card-adoption-pipeline">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5" />
            Adoption Levels
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex items-center gap-2">
              Failed to load adoption pipeline data.
              <Button variant="outline" size="sm" onClick={() => refetch()} data-testid="button-retry-adoption-pipeline">
                <RefreshCw className="h-3.5 w-3.5 mr-1" /> Retry
              </Button>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card id="section-adoption-pipeline" data-testid="card-adoption-pipeline">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2">
          <Layers className="h-5 w-5" />
          Adoption Levels
        </CardTitle>
        <Select value={speciesFilter} onValueChange={(v) => setSpeciesFilter(v as SpeciesFilter)}>
          <SelectTrigger className="w-[140px]" data-testid="select-species-adoption-pipeline">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Species</SelectItem>
            <SelectItem value="DOG">Dogs</SelectItem>
            <SelectItem value="CAT">Cats</SelectItem>
            <SelectItem value="OTHER">Other</SelectItem>
          </SelectContent>
        </Select>
      </CardHeader>
      <CardContent>
        {!showLocationPanels && latest && (
          <div className="mb-4" data-testid="adoption-pipeline-stats">
            <p className="text-xs text-muted-foreground mb-1" data-testid="text-adoption-stats-hint">
              All records within selected date range
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
              <StatCard label="Total" value={latest.total} testId="stat-adoption-total" />
              <StatCard label={LEVEL_LABELS.L1} value={latest.L1} color={LEVEL_COLORS.L1} testId="stat-adoption-l1" />
              <StatCard label={LEVEL_LABELS.L2} value={latest.L2} color={LEVEL_COLORS.L2} testId="stat-adoption-l2" />
              <StatCard label={LEVEL_LABELS.L3} value={latest.L3} color={LEVEL_COLORS.L3} testId="stat-adoption-l3" />
              <StatCard label={LEVEL_LABELS.L4} value={latest.L4} color={LEVEL_COLORS.L4} testId="stat-adoption-l4" />
              <StatCard label={LEVEL_LABELS.unknown} value={latest.unknown} testId="stat-adoption-unknown" />
            </div>
          </div>
        )}

        {showLocationPanels ? (
          <div className={`grid gap-4 grid-cols-1 md:grid-cols-2`}>
            {showSystem && (
              <SingleLocationChart
                days={days}
                comparisonMode={comparisonMode}
                speciesFilter={speciesFilter}
                label="System-wide"
              />
            )}
            {selectedLocations.map((locCode, idx) => (
              <SingleLocationChart
                key={locCode}
                days={days}
                location={locCode}
                comparisonMode={comparisonMode}
                speciesFilter={speciesFilter}
                label={getLocationLabel(locCode)}
                color={LOCATION_SERIES_COLORS[idx % LOCATION_SERIES_COLORS.length]}
              />
            ))}
          </div>
        ) : hasData ? (
          <AdoptionChartWithLegend chartData={chartData} hasPrev={hasPrev} comparisonMode={comparisonMode} />
        ) : (
          <div className="flex items-center justify-center h-[200px] text-muted-foreground" data-testid="text-adoption-empty">
            No adoption pipeline data available for the selected period.
          </div>
        )}
      </CardContent>
    </Card>
  );
}
